import type { CategoryId, CommitmentItem, CommitmentStatus, MonthHistory } from '../types';
import { normalizeCategory } from '../types';

export interface PeriodMonth {
  m: number;
  y: number;
}

export function getPeriodMonths(count: number, currentMonth: number, currentYear: number): PeriodMonth[] {
  const safeCount = Math.min(Math.max(Math.floor(count) || 1, 1), 12);
  return Array.from({ length: safeCount }, (_, index) => {
    const date = new Date(currentYear, currentMonth - safeCount + index, 1);
    return { m: date.getMonth() + 1, y: date.getFullYear() };
  });
}

export function isPeriodRow(row: { month?: number; year?: number }, periods: PeriodMonth[]) {
  return periods.some(period => row.month === period.m && row.year === period.y);
}

export function commitmentItemsFromRows(rows: {
  invoices?: any[];
  recurringOccurrences?: any[];
  installmentOccurrences?: any[];
  oneOffExpenses?: any[];
}): CommitmentItem[] {
  const items: CommitmentItem[] = [];

  for (const invoice of rows.invoices ?? []) {
    const status: CommitmentStatus = invoice.status === 'paid'
      ? 'paid'
      : invoice.status === 'overdue' ? 'overdue' : 'pending';
    items.push({
      id: invoice.id,
      type: 'invoice',
      name: invoice.card?.name ?? 'Fatura',
      amount: Number(invoice.total_amount ?? 0),
      dueDate: invoice.due_date,
      status,
      month: invoice.month,
      year: invoice.year,
      category: normalizeCategory(invoice.category),
      meta: invoice,
    });
  }

  for (const occurrence of rows.recurringOccurrences ?? []) {
    if (occurrence.status === 'cancelled') continue;
    items.push({
      id: occurrence.id,
      type: 'recurring',
      name: occurrence.recurring_expense?.name ?? 'Conta',
      amount: Number(occurrence.amount ?? 0),
      amountMissing: occurrence.amount == null,
      dueDate: occurrence.due_date,
      status: occurrence.status === 'paid' ? 'paid' : occurrence.status === 'overdue' ? 'overdue' : 'pending',
      month: occurrence.month,
      year: occurrence.year,
      category: normalizeCategory(occurrence.recurring_expense?.category),
      meta: occurrence,
    });
  }

  for (const occurrence of rows.installmentOccurrences ?? []) {
    if (occurrence.status === 'cancelled') continue;
    const installment = occurrence.installment;
    items.push({
      id: occurrence.id,
      type: 'installment',
      name: installment?.name ?? 'Parcela',
      amount: Number(occurrence.amount ?? 0),
      status: occurrence.status === 'paid' ? 'paid' : 'pending',
      month: occurrence.month,
      year: occurrence.year,
      category: normalizeCategory(installment?.category),
      meta: occurrence,
    });
  }

  for (const expense of rows.oneOffExpenses ?? []) {
    if (expense.status === 'cancelled') continue;
    items.push({
      id: expense.id,
      type: 'one_off',
      name: expense.name,
      amount: Number(expense.amount ?? 0),
      dueDate: expense.due_date,
      status: expense.status === 'paid' ? 'paid' : expense.status === 'overdue' ? 'overdue' : 'pending',
      month: expense.month,
      year: expense.year,
      category: normalizeCategory(expense.category),
      meta: expense,
    });
  }

  return items;
}

/** Projection used by the Lançamentos audit timeline, including received income. */
export function launchItemsFromRows(rows: {
  incomes?: any[];
  invoices?: any[];
  recurringOccurrences?: any[];
  installmentOccurrences?: any[];
  oneOffExpenses?: any[];
}): CommitmentItem[] {
  const items = commitmentItemsFromRows(rows);
  for (const income of rows.incomes ?? []) {
    items.push({
      id: income.id,
      type: 'income',
      name: income.name || 'Receita',
      amount: Number(income.amount ?? 0),
      dueDate: income.received_at,
      status: 'paid',
      month: income.month,
      year: income.year,
      meta: income,
    });
  }
  return items.sort((a, b) => {
    const parseDate = (value?: string) => value ? new Date(`${value}${value.length === 10 ? 'T12:00:00' : ''}`).getTime() : 0;
    return parseDate(b.dueDate) - parseDate(a.dueDate);
  });
}

export function historyFromRows(
  periods: PeriodMonth[],
  rows: { incomes?: any[]; commitments: CommitmentItem[] },
): MonthHistory[] {
  return periods.map(({ m, y }) => ({
    month: m,
    year: y,
    income: (rows.incomes ?? [])
      .filter(item => item.month === m && item.year === y)
      .reduce((sum, item) => sum + Number(item.amount ?? 0), 0),
    expenses: rows.commitments
      .filter(item => item.month === m && item.year === y)
      .reduce((sum, item) => sum + Number(item.amount ?? 0), 0),
  }));
}

export function categoryTotalsForItems(items: CommitmentItem[]): Record<CategoryId, number> {
  const totals = {} as Record<CategoryId, number>;
  for (const item of items) {
    if (item.type === 'invoice') continue;
    const category = item.category ?? 'outros';
    totals[category] = (totals[category] ?? 0) + item.amount;
  }
  return totals;
}
